import { useQuery } from '@tanstack/react-query';

import PortainerError from '@/portainer/error';
import axios, { parseAxiosError } from '@/react/portainer/services/axios/axios';
import { isAxiosError } from '@/react/portainer/services/axios/utils/isAxiosError';
import { withError } from '@/react-tools/react-query';

import { buildUrl } from '../buildUrl';
import { User, UserId } from '../types';

import { userQueryKeys } from './queryKeys';

const DEFAULT_RETRY_COUNT = 3;

export class UserNotFoundError extends PortainerError {}

export function useUser(
  id: UserId,
  { staleTime, silent }: { staleTime?: number; silent?: boolean } = {}
) {
  return useQuery(
    userQueryKeys.user(id),
    ({ signal }) => getUser(id, { signal }),
    {
      ...(silent ? {} : withError('Unable to retrieve user details')),
      retry: (failureCount, error) =>
        !(error instanceof UserNotFoundError) &&
        failureCount < DEFAULT_RETRY_COUNT,
      staleTime,
    }
  );
}

export async function getUser(
  id: UserId,
  options: { signal?: AbortSignal } = {}
) {
  try {
    const { data: user } = await axios.get<User>(buildUrl(id), {
      signal: options.signal,
    });

    return user;
  } catch (e) {
    const err = parseAxiosError(e as Error, 'Unable to retrieve user details');
    if (isAxiosError(e) && e.response?.status === 404) {
      throw new UserNotFoundError(err.message, err.err);
    }

    throw err;
  }
}
